import React from "react";

/**
 * TaskCircle Component
 * 
 * Renders:
 * - Empty ring for "Open"/"On Hold"/"Closed"
 * - Filled orange dot for "In Progress"
 * - Filled green dot for "Resolved"
 * 
 * Ring border color maps to priority:
 * - Critical: red
 * - High: orange
 * - Medium: blue
 * - Low: gray
 * 
 * Includes a smooth fill transition animation on click/state changes.
 */
export function TaskCircle({ status, priority, onClick, isRecurring = false }) {
  // Map priority to border color
  let borderColor = "border-gray-500 hover:border-gray-600";
  switch (priority) {
    case "Critical": // keeping for legacy
    case "High":
    case "P1":
      borderColor = "border-red-600 hover:border-red-700";
      break;
    case "Medium":
    case "P2":
      borderColor = "border-orange-500 hover:border-orange-600";
      break;
    case "Low":
    case "P3":
    case "P4":
    default:
      borderColor = "border-gray-500 hover:border-gray-600";
      break;
  }

  // Determine fill color and visibility based on status
  let fillBg = "";
  let isFilled = false;

  if (status === "In Progress") {
    fillBg = "bg-orange-500";
    isFilled = true;
  } else if (status === "Resolved") {
    fillBg = "bg-green-500";
    isFilled = true;
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <button
        type="button"
        onClick={onClick}
        style={{ width: "16px", height: "16px", minWidth: "16px", minHeight: "16px" }}
        className={`rounded-full border-2 ${borderColor} flex items-center justify-center transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 shrink-0 bg-transparent`}
        aria-label={`Task status: ${status || "Open"}, priority: ${priority || "Low"}${isRecurring ? ", recurring" : ""}`}
      >
        <span
          style={{ width: "8px", height: "8px" }}
          className={`rounded-full ${fillBg} transition-all duration-200 ease-in-out transform ${
            isFilled ? "scale-100 opacity-100" : "scale-0 opacity-0"
          }`}
        />
      </button>
      {isRecurring && (
        <span className="text-[9px] text-gray-400 select-none leading-none" title="Recurring task">🔄</span>
      )}
    </div>
  );
}
